import * as React from 'react';
import { memo, useCallback, useState } from 'react';

import { Select, Switch } from 'antd';
import { useTranslation } from 'react-i18next';

import { INotificationPrefs, INotificationPrefsInput } from 'core/hooks';

import { toSelectValue } from 'core/utils/notificationAlarm';

import * as S from './NotificationCenter.styled';
import { kindMeta } from './notificationTypes';
import { TelegramLinkBlock } from './TelegramLinkBlock';

interface IProps {
  prefs: INotificationPrefs | null;
  /** Настройки не загрузились: показать причину, а не вечное «Загрузка…». */
  loadError?: boolean;
  saving: boolean;
  audioUnlocked: boolean;
  onSave: (input: INotificationPrefsInput) => Promise<boolean>;
  onPreviewSound: () => Promise<boolean>;
  onStartTelegramLink: () => Promise<{
    deepLink: string;
    expiresAt: string;
  } | null>;
  onDropTelegramLink: () => Promise<void>;
  onRefetch: () => void;
}

const HOURS = Array.from({ length: 24 }, (_, hour) => {
  const value = `${String(hour).padStart(2, '0')}:00`;
  return { value, label: value };
});

const REPEATS = [5, 10, 15, 30, 60];

/**
 * Настройки уведомлений — вторая «страница» той же панели (ТЗ 0068).
 *
 * 🔴 Живёт ЗДЕСЬ, а не на отдельном маршруте, ровно из-за звука: браузер
 * разрешает `play()` только внутри обработчика клика, поэтому переключатель
 * обязан быть тем же жестом, что и проверка сигнала. Разнеси мы их по разным
 * экранам — человек включал бы звук на одной странице, ждал на другой, и
 * после первого F5 это разваливалось бы без объяснимой причины.
 */
export const NotificationSettingsPane = memo(
  ({
    prefs,
    loadError,
    saving,
    audioUnlocked,
    onSave,
    onPreviewSound,
    onStartTelegramLink,
    onDropTelegramLink,
    onRefetch,
  }: IProps) => {
    const { t } = useTranslation();
    const [saved, setSaved] = useState(false);
    const [failed, setFailed] = useState(false);
    const [soundBlocked, setSoundBlocked] = useState(false);
    /**
     * 🔴 Черновик поверх серверных настроек.
     *
     * Без него два быстрых клика теряли первый: новый массив строился из
     * `prefs`, а те обновляются только после ответа мутации и повторного
     * запроса — второй клик читал устаревший список и возвращал только что
     * снятую галку обратно.
     */
    const [draft, setDraft] = useState<INotificationPrefs | null>(null);
    const view = draft ?? prefs;

    const commit = useCallback(
      async (
        input: INotificationPrefsInput,
        optimistic?: INotificationPrefs,
      ) => {
        if (optimistic) setDraft(optimistic);
        setFailed(false);
        let ok = false;
        try {
          ok = await onSave(input);
        } catch {
          // Сетевой сбой внутри async-обработчика antd иначе стал бы
          // unhandled rejection, а переключатель просто не двигался бы.
          ok = false;
        }
        setSaved(ok);
        setFailed(!ok);
        setDraft(null);
        onRefetch();
        // Успех показываем строкой ПОД блоком, а не тостом: тост из шапки
        // ложится поверх открытой панели и закрывает то, что человек правит.
        if (ok) window.setTimeout(() => setSaved(false), 2000);
      },
      [onRefetch, onSave],
    );

    const toggleSound = useCallback(
      async (checked: boolean) => {
        if (checked) {
          // Разблокировка и образец — в ТОМ ЖЕ клике: это и разрешение
          // браузера, и честное «вот так это звучит».
          const ok = await onPreviewSound();
          setSoundBlocked(!ok);
        } else {
          setSoundBlocked(false);
        }
        await commit({ soundEnabled: checked });
      },
      [commit, onPreviewSound],
    );

    if (!view) {
      // Отказ загрузки раньше давал вечное «Загрузка…» — состояние, из
      // которого человек не мог понять, что произошло.
      return (
        <S.Pane>
          {loadError
            ? t('notifications.settings.error')
            : t('notifications.settings.loading')}
        </S.Pane>
      );
    }

    const quietOn = Boolean(view.quietFrom && view.quietTo);

    return (
      <S.Pane>
        <S.Section>
          <S.SectionTitle>{t('notifications.settings.sound')}</S.SectionTitle>
          <S.Field>
            <span>
              {t('notifications.sound.label')}
              <S.FieldHint>{t('notifications.sound.hint')}</S.FieldHint>
            </span>
            <Switch
              checked={view.soundEnabled}
              loading={saving}
              onChange={toggleSound}
            />
          </S.Field>

          {view.soundEnabled && (
            <>
              <S.Field>
                <span>{t('notifications.sound.repeat')}</span>
                <Select
                  size="small"
                  style={{ width: 120 }}
                  value={view.repeatAfterMinutes}
                  onChange={(value: number) =>
                    commit({ repeatAfterMinutes: value })
                  }
                  options={REPEATS.map((minutes) => ({
                    value: minutes,
                    label: t('notifications.sound.repeatMinutes', {
                      count: minutes,
                    }),
                  }))}
                />
              </S.Field>
              <S.InlineActions>
                <S.TextButton type="button" onClick={onPreviewSound}>
                  {t('notifications.sound.test')}
                </S.TextButton>
                {!audioUnlocked && (
                  <S.FieldHint>{t('notifications.sound.locked')}</S.FieldHint>
                )}
              </S.InlineActions>
              {soundBlocked && (
                <S.Failed>{t('notifications.sound.blocked')}</S.Failed>
              )}
            </>
          )}

          <S.Field>
            <span>{t('notifications.sound.quietHours')}</span>
            <Switch
              size="small"
              checked={quietOn}
              onChange={(checked) =>
                commit(
                  checked
                    ? { quietFrom: '22:00', quietTo: '08:00' }
                    : { clearQuietHours: true },
                )
              }
            />
          </S.Field>
          {quietOn && (
            <S.InlineActions>
              {/* Обе границы шлём вместе: сервер отвергает половину окна —
                  одна граница сохранялась бы, показывалась и не действовала. */}
              <Select
                size="small"
                style={{ width: 96 }}
                value={toSelectValue(view.quietFrom)}
                options={HOURS}
                onChange={(value: string) =>
                  commit({
                    quietFrom: value,
                    quietTo: toSelectValue(view.quietTo) ?? '08:00',
                  })
                }
              />
              <span>—</span>
              <Select
                size="small"
                style={{ width: 96 }}
                value={toSelectValue(view.quietTo)}
                options={HOURS}
                onChange={(value: string) =>
                  commit({
                    quietFrom: toSelectValue(view.quietFrom) ?? '22:00',
                    quietTo: value,
                  })
                }
              />
            </S.InlineActions>
          )}
        </S.Section>

        <S.Section>
          <S.SectionTitle>{t('notifications.settings.kinds')}</S.SectionTitle>
          {view.availableKinds.map((kind) => (
            <S.Field key={kind.key}>
              {/* 🔴 Подпись даёт ФРОНТ, а не сервер. Серверный `label` идёт
                  через `gettext_lazy` и переводится локалью ПРОЦЕССА, а не
                  языком интерфейса: на польском ERP названия видов приезжали
                  по-русски. Поймано глазами — локаль-гейт проверяет ключи в
                  коде и о тексте с сервера ничего не знает. */}
              <span>{t(kindMeta(kind.key).labelKey)}</span>
              <Switch
                size="small"
                checked={kind.enabled}
                onChange={(checked) => {
                  const next = checked
                    ? [...view.enabledKinds, kind.key]
                    : view.enabledKinds.filter((key) => key !== kind.key);
                  commit(
                    { enabledKinds: next },
                    {
                      ...view,
                      enabledKinds: next,
                      availableKinds: view.availableKinds.map((item) =>
                        item.key === kind.key
                          ? { ...item, enabled: checked }
                          : item,
                      ),
                    },
                  );
                }}
              />
            </S.Field>
          ))}
        </S.Section>

        <S.Section>
          <S.SectionTitle>{t('notifications.settings.robot')}</S.SectionTitle>
          <S.FieldHint>{t('notifications.robot.hint')}</S.FieldHint>
          {view.availableRobotRules.map((rule) => (
            <S.Field key={rule.key}>
              <span>
                {/* Тот же случай: переводим сами, серверный `label` —
                    запасной вариант для правила, которого фронт ещё не
                    знает. */}
                {t(`notifications.robot.rule.${rule.key}`, {
                  defaultValue: rule.label,
                })}
                {!rule.available && (
                  // Подписка на выключенное правило не должна выглядеть как
                  // работающая настройка.
                  <S.FieldHint>{t('notifications.robot.off')}</S.FieldHint>
                )}
              </span>
              <Switch
                size="small"
                checked={rule.enabled}
                onChange={(checked) => {
                  const next = checked
                    ? [...view.robotRules, rule.key]
                    : view.robotRules.filter((key) => key !== rule.key);
                  commit(
                    { robotRules: next },
                    {
                      ...view,
                      robotRules: next,
                      availableRobotRules: view.availableRobotRules.map(
                        (item) =>
                          item.key === rule.key
                            ? { ...item, enabled: checked }
                            : item,
                      ),
                    },
                  );
                }}
              />
            </S.Field>
          ))}
        </S.Section>

        <TelegramLinkBlock
          prefs={view}
          onStart={onStartTelegramLink}
          onDrop={onDropTelegramLink}
          onRefetch={onRefetch}
        />

        {saved && <S.Saved>{t('notifications.settings.saved')}</S.Saved>}
        {failed && (
          <S.Failed>{t('notifications.settings.saveFailed')}</S.Failed>
        )}
      </S.Pane>
    );
  },
);

NotificationSettingsPane.displayName = 'NotificationSettingsPane';
